import { ValueType } from '@srm/ui/types/ValueType';
import dayjs from '@srm/ui/lib/dayjs';
import { parseRuDate, parseRuDateTime } from '@utils/date';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';
import { IAttachColumn, IAttachRow, IAttachSort } from './AttachGrid.types';
import { rowValue } from './attachRowView';

/* Бэкенд attachData игнорирует sort/skip/take (отдаёт все строки), поэтому сортировку
   и пейджинг делаем на клиенте. */

const isDateType = (dt: ValueType): boolean =>
    dt === ValueType.Date || dt === ValueType.DateTime || dt === ValueType.NoSecDateTime;
const isVersionField = (field: string): boolean => /Version/i.test(field);

/** Размер из «1.46 МБ» → байты (для числовой сортировки колонки FileSize). */
const SIZE_UNITS: Record<string, number> = {
    б: 1,
    кб: 1024,
    мб: 1024 ** 2,
    гб: 1024 ** 3,
    тб: 1024 ** 4,
    b: 1,
    kb: 1024,
    mb: 1024 ** 2,
    gb: 1024 ** 3,
    tb: 1024 ** 4,
};
function fileSizeToBytes(value: unknown): number {
    const m = String(value)
        .trim()
        .toLowerCase()
        .replace(',', '.')
        .match(/^([\d.]+)\s*([а-яa-z]*)$/i);
    if (!m) return NaN;
    return parseFloat(m[1]) * (SIZE_UNITS[m[2] || 'б'] ?? 1);
}

function dateMs(value: unknown): number {
    if (value == null || value === '') return NaN;
    const s = String(value);
    const ru = parseRuDateTime(s) ?? parseRuDate(s);
    const d = ru ? dayjs(ru) : dayjs(toLocalDate(value as Date | string | number));
    return d.isValid() ? d.valueOf() : NaN;
}

function compareValues(a: unknown, b: unknown, col?: IAttachColumn): number {
    const aEmpty = a == null || a === '';
    const bEmpty = b == null || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return -1;
    if (bEmpty) return 1;

    if (col && isVersionField(col.field)) {
        return (parseInt(String(a), 10) || 0) - (parseInt(String(b), 10) || 0);
    }
    if (col?.dataType === ValueType.FileSize) {
        const ab = fileSizeToBytes(a);
        const bb = fileSizeToBytes(b);
        if (!Number.isNaN(ab) && !Number.isNaN(bb)) return ab - bb;
    }
    if (col && isDateType(col.dataType)) {
        const am = dateMs(a);
        const bm = dateMs(b);
        if (!Number.isNaN(am) && !Number.isNaN(bm)) return am - bm;
    }
    // Численно сравниваем только объявленные числовыми колонки — иначе текстовые
    // значения вида '007'/'42' и смешанные '10'/'9a' давали нетранзитивный порядок.
    if (col?.filterKind === 'number') {
        const an = Number(a);
        const bn = Number(b);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
    }
    // numeric:true сохраняет «человеческую» сортировку чисел внутри строк, но стабильно.
    return String(a).localeCompare(String(b), 'ru', { numeric: true });
}

/** Стабильная многоуровневая клиентская сортировка строк по активным ключам. */
export function sortRows(rows: IAttachRow[], sort: IAttachSort[], columns: IAttachColumn[]): IAttachRow[] {
    if (!sort.length) return rows;
    const colMap = new Map(columns.map((c) => [c.field, c]));
    return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
            for (const s of sort) {
                const cmp = compareValues(rowValue(a.row, s.field), rowValue(b.row, s.field), colMap.get(s.field));
                if (cmp !== 0) return s.dir === 'desc' ? -cmp : cmp;
            }
            return a.index - b.index; // стабильность
        })
        .map((x) => x.row);
}
